import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { captureException } from "@sentry/react";

import { createNexaThread } from "./api";
import {
  GTC_HEADER_HEIGHT,
  GTC_NAV_WIDTH_COLLAPSED,
  GTC_NAV_WIDTH_OPEN,
  NEXA_FLOATING_WIDTH,
  NEXA_SIDEBAR_DEFAULT_WIDTH,
  NEXA_SIDEBAR_MAX_WIDTH,
  NEXA_SIDEBAR_MIN_VIEWPORT,
  NEXA_SIDEBAR_MIN_WIDTH,
  type NexaLayout,
} from "./constants";
import NexaFab from "./components/NexaFab";
import NexaPanel from "./NexaPanel";
import ThreadList from "./components/ThreadList";
import { FAB_SIZE, useFabPosition } from "./useFabPosition";
import { clearHandoff, peekHandoff, stashForFullscreen } from "./handoff";

interface LauncherState {
  profiledata: { data?: { fullName?: string } };
  sidebar?: { isOpen?: boolean };
}

interface NexaLauncherProps {
  contextLabel?: string;
}

type OpenLayout = Extract<NexaLayout, "floating" | "sidebar">;

const FLOATING_MAX_HEIGHT = 620;
const PANEL_GUTTER = 8;
const PANEL_OFFSET = 12;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), Math.max(min, max));

const NexaLauncher = ({ contextLabel }: NexaLauncherProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [layout, setLayout] = useState<OpenLayout | "closed">("closed");
  const [threadId, setThreadId] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(NEXA_SIDEBAR_DEFAULT_WIDTH);
  const [threadsRefreshKey, setThreadsRefreshKey] = useState(0);
  const [threadError, setThreadError] = useState(false);
  const [viewport, setViewport] = useState(() => ({
    width: typeof window === "undefined" ? 0 : window.innerWidth,
    height: typeof window === "undefined" ? 0 : window.innerHeight,
  }));

  const profile = useSelector((state: LauncherState) => state.profiledata?.data);
  const navOpen = useSelector((state: LauncherState) => state.sidebar?.isOpen ?? false);
  const userName = profile?.fullName?.split(" ")[0];

  const contentLeft = (navOpen ? GTC_NAV_WIDTH_OPEN : GTC_NAV_WIDTH_COLLAPSED) + PANEL_GUTTER;

  const fab = useFabPosition({ minX: contentLeft });

  useEffect(() => {
    const onResize = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const { pendingLayout, threadId: handedOver } = peekHandoff();
    clearHandoff();
    if (!pendingLayout) return;
    if (handedOver) setThreadId(handedOver);
    setLayout(pendingLayout);
  }, []);

  const narrow = viewport.width > 0 && viewport.width < NEXA_SIDEBAR_MIN_VIEWPORT;
  const effectiveLayout: OpenLayout | "closed" = layout === "sidebar" && narrow ? "floating" : layout;

  const refreshThreads = useCallback(() => setThreadsRefreshKey((k) => k + 1), []);

  const threadUnavailable = useCallback(() => {
    setThreadId(null);
    setHistoryOpen(true);
  }, []);

  const threadDeleted = useCallback(
    (deletedId: string) => {
      if (deletedId === threadId) threadUnavailable();
    },
    [threadId, threadUnavailable]
  );

  const createThread = useCallback(async () => {
    setThreadError(false);
    try {
      const id = await createNexaThread();
      setThreadId(id);
      setThreadsRefreshKey((k) => k + 1);
      return id;
    } catch (err) {
      captureException(err);
      setThreadError(true);
      throw err;
    }
  }, []);

  const open = useCallback((next: OpenLayout) => setLayout(next), []);

  const changeLayout = useCallback(
    (next: NexaLayout) => {
      if (next === "fullscreen") {
        stashForFullscreen(pathname, threadId);
        setLayout("closed");
        navigate("/nexa-ai");
        return;
      }
      setLayout(next);
    },
    [navigate, pathname, threadId]
  );

  const newChat = useCallback(() => {
    setHistoryOpen(false);
    setThreadId(null);
    setThreadError(false);
  }, []);

  const resizeRef = useRef<{ startX: number; startWidth: number } | null>(null);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!resizeRef.current) return;
      const { startX, startWidth } = resizeRef.current;
      setSidebarWidth(clamp(startWidth + (startX - e.clientX), NEXA_SIDEBAR_MIN_WIDTH, NEXA_SIDEBAR_MAX_WIDTH));
    };
    const onUp = () => {
      resizeRef.current = null;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const floatingStyle = useMemo(() => {
    const width = Math.min(NEXA_FLOATING_WIDTH, Math.max(280, viewport.width - contentLeft - PANEL_GUTTER));
    const height = Math.min(FLOATING_MAX_HEIGHT, Math.max(320, viewport.height - GTC_HEADER_HEIGHT - PANEL_GUTTER * 2));
    const topLimit = GTC_HEADER_HEIGHT + PANEL_GUTTER;
    return {
      width,
      height,
      left: clamp(fab.position.x + FAB_SIZE - width, contentLeft, viewport.width - width - PANEL_GUTTER),
      top: clamp(fab.position.y - height - PANEL_OFFSET, topLimit, viewport.height - height - PANEL_GUTTER),
    };
  }, [fab.position, viewport, contentLeft]);

  const onWorkspaceRoute = pathname.startsWith("/nexa-ai");

  const panel = (
    <NexaPanel
      threadId={threadId}
      userName={userName}
      contextLabel={contextLabel}
      activeLayout={effectiveLayout === "closed" ? "floating" : effectiveLayout}
      threadError={threadError}
      onRetryThread={createThread}
      onCreateThread={createThread}
      onTurnComplete={refreshThreads}
      onLayoutChange={changeLayout}
      onOpenHistory={() => setHistoryOpen(true)}
      onNewChat={newChat}
      onClose={() => setLayout("closed")}
      onThreadUnavailable={threadUnavailable}
    />
  );

  const historyDrawer = historyOpen ? (
    <div className="tw-absolute tw-inset-0 tw-z-[45] tw-flex">
      <div className="tw-flex tw-h-full tw-w-[84%] tw-max-w-[330px] tw-flex-col tw-bg-white tw-shadow-2xl">
        <div className="tw-flex tw-shrink-0 tw-justify-end tw-p-2">
          <button
            type="button"
            onClick={() => setHistoryOpen(false)}
            aria-label="Close history"
            className="tw-grid tw-h-8 tw-w-8 tw-place-items-center tw-rounded-full tw-bg-gray-100 tw-text-gray-600 hover:tw-bg-gray-200"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <ThreadList
          activeThreadId={threadId}
          refreshKey={threadsRefreshKey}
          onNewChat={newChat}
          onDeleted={threadDeleted}
          onSelect={(id) => {
            setThreadId(id);
            setHistoryOpen(false);
          }}
        />
      </div>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div className="tw-flex-1 tw-bg-[rgba(30,27,75,0.14)]" onClick={() => setHistoryOpen(false)} />
    </div>
  ) : null;

  if (onWorkspaceRoute) return null;

  if (effectiveLayout === "closed") {
    return (
      <NexaFab
        onOpen={() => open("floating")}
        position={fab.position}
        dragging={fab.dragging}
        onPointerDown={fab.onPointerDown}
        onPointerMove={fab.onPointerMove}
        onPointerUp={fab.onPointerUp}
      />
    );
  }

  const isFloating = effectiveLayout === "floating";

  return (
    <div
      className={`tw-fixed tw-flex tw-bg-white tw-shadow-2xl ${
        isFloating ? "tw-z-50 tw-overflow-hidden tw-rounded-2xl tw-border tw-border-[#E4E2F4]" : "tw-z-[42]"
      }`}
      style={
        isFloating
          ? floatingStyle
          : { top: GTC_HEADER_HEIGHT, right: 0, bottom: 0, width: sidebarWidth }
      }
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        role="separator"
        aria-orientation="vertical"
        title="Drag to resize"
        onMouseDown={(e) => {
          if (isFloating) return;
          e.preventDefault();
          resizeRef.current = { startX: e.clientX, startWidth: sidebarWidth };
        }}
        className={`tw-w-1.5 tw-shrink-0 tw-cursor-col-resize tw-bg-[#F2F1F8] hover:tw-bg-[#C7CDFB] ${isFloating ? "tw-hidden" : ""}`}
      />
      <div className="tw-relative tw-min-w-0 tw-flex-1">
        {panel}
        {historyDrawer}
      </div>
    </div>
  );
};

export default NexaLauncher;
